import { useCallback, useEffect, useRef, useState } from 'react'
import {
  canRedo as historyCanRedo,
  canUndo as historyCanUndo,
  createHistory,
  recordEdit,
  redo as historyRedo,
  undo as historyUndo,
  type EditKind,
  type HistoryEntry,
  type HistoryState
} from '../lib/editorHistory'

/**
 * Any code path that replaces a chapter's content while keeping its id MUST call
 * resetChapter, or the cached stack will still hold the pre-replacement content and one undo
 * will restore it. Current call sites: handleMergeChapters and handlePasteStorySubmit in
 * App.tsx. handleDeleteChapter calls forgetChapter instead.
 */
export interface ManuscriptUndoApi {
  canUndo: boolean
  canRedo: boolean
  record: (chapterId: string, entry: HistoryEntry, kind: EditKind) => void
  undo: (chapterId: string) => HistoryEntry | null
  redo: (chapterId: string) => HistoryEntry | null
  resetChapter: (chapterId: string, content: string) => void
  forgetChapter: (chapterId: string) => void
}

/**
 * Per-chapter undo history. Must be called from App, not StoryCanvas: the canvas is keyed
 * by chapter id and fully remounts on chapter switch, which would discard the stack.
 */
export function useManuscriptUndo(
  activeChapterId: string | null,
  activeContent: string
): ManuscriptUndoApi {
  const historiesRef = useRef<Record<string, HistoryState>>({})
  const [flags, setFlags] = useState({ canUndo: false, canRedo: false })

  const syncFlags = useCallback((chapterId: string | null): void => {
    const state = chapterId ? historiesRef.current[chapterId] : undefined
    setFlags({
      canUndo: state ? historyCanUndo(state) : false,
      canRedo: state ? historyCanRedo(state) : false
    })
  }, [])

  // Seed a chapter's history the first time it becomes active, then keep the button flags
  // in step whenever the user switches chapters.
  useEffect(() => {
    if (!activeChapterId) {
      // Clearing flags when there is no active chapter, not synchronizing from a value
      // computed during render.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFlags({ canUndo: false, canRedo: false })
      return
    }
    if (!historiesRef.current[activeChapterId]) {
      historiesRef.current[activeChapterId] = createHistory({
        content: activeContent,
        caretOffset: -1
      })
    }
    syncFlags(activeChapterId)
    // activeContent is deliberately omitted: it changes on every keystroke, and seeding only
    // ever needs the content as it stood when the chapter was first opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChapterId, syncFlags])

  const record = useCallback(
    (chapterId: string, entry: HistoryEntry, kind: EditKind): void => {
      const existing = historiesRef.current[chapterId]
      historiesRef.current[chapterId] = existing
        ? recordEdit(existing, entry, kind, Date.now())
        : createHistory(entry)
      syncFlags(chapterId)
    },
    [syncFlags]
  )

  const undo = useCallback(
    (chapterId: string): HistoryEntry | null => {
      const existing = historiesRef.current[chapterId]
      if (!existing || !historyCanUndo(existing)) return null
      const next = historyUndo(existing)
      historiesRef.current[chapterId] = next
      syncFlags(chapterId)
      return next.present
    },
    [syncFlags]
  )

  const redo = useCallback(
    (chapterId: string): HistoryEntry | null => {
      const existing = historiesRef.current[chapterId]
      if (!existing || !historyCanRedo(existing)) return null
      const next = historyRedo(existing)
      historiesRef.current[chapterId] = next
      syncFlags(chapterId)
      return next.present
    },
    [syncFlags]
  )

  // Chapter-level operations change a chapter's content behind the editor's back. Merge is the
  // dangerous one: it keeps sorted[0]'s id while replacing its content with every chapter
  // joined together, so without this the cached stack still holds the pre-merge text — and one
  // Cmd+Z after the next keystroke would restore it, discarding the merged-in prose whose rows
  // the merge save has already deleted.
  const resetChapter = useCallback(
    (chapterId: string, content: string): void => {
      historiesRef.current[chapterId] = createHistory({ content, caretOffset: -1 })
      syncFlags(chapterId)
    },
    [syncFlags]
  )

  const forgetChapter = useCallback((chapterId: string): void => {
    delete historiesRef.current[chapterId]
  }, [])

  return {
    canUndo: flags.canUndo,
    canRedo: flags.canRedo,
    record,
    undo,
    redo,
    resetChapter,
    forgetChapter
  }
}
